import React, { useCallback } from 'react';

// Images
import GoogleLogo from 'Shared/images/google-logo.svg';
import Office365Logo from 'Shared/images/office365-logo.svg';
import SlackLogo from 'Shared/images/slackLogo.svg';

// Components
import { WpButton, Color, Variant, Size } from 'Wpds/components/button';
import WpIcon from 'Wpds/components/icon/WpIcon';
import { trackClickedConnectApp } from 'Shared/tracking/tracking';

// Styles
import './OAuthButton.scss';

// Types
import { DataSourceSlugs } from 'Shared/constants/types';
import { Illustrations } from 'Wpds/components/illustration';
import { useApp } from 'Shared/utils/hooks/use-app';

const DEFAULT_COLOR = 'gray';
const DEFAULT_SIZE = 'lg';
const DEFAULT_VARIANT = 'secondary';

type OauthProvider = 'google' | 'office365' | 'slack' | 'pi';

type Props = {
  oauthProvider: OauthProvider;
  text: string;
  variant?: Variant;
  size?: Size;
  color?: Color;
  block?: boolean;
  qaTest?: string;
  withCalendar?: boolean;
  iconType?: 'logo' | 'icon' | 'none';
  redirectUrl?: string;
  onClick?: Function;
  additionalQueryParams?: string;
};

const OAuthButton = ({
  oauthProvider,
  text,
  variant = DEFAULT_VARIANT,
  size = DEFAULT_SIZE,
  color = DEFAULT_COLOR,
  block = true,
  withCalendar = false,
  qaTest,
  iconType = 'logo',
  redirectUrl,
  onClick,
  additionalQueryParams = '',
}: Props) => {
  const {
    appId: id,
    app: {
      routes: { googleSignUpUrl, office365SignUpUrl, googleCalendarSignupProfileScopeUrl, office365CalendarSignupUrl },
    },
  } = useApp();

  const getAuthUrl = () => {
    if (withCalendar && oauthProvider !== 'slack') {
      switch (oauthProvider) {
        case 'google':
          return googleCalendarSignupProfileScopeUrl;
        case 'office365':
          return office365CalendarSignupUrl;
        default:
          return;
      }
    }

    switch (oauthProvider) {
      case 'google':
        return googleSignUpUrl;
      case 'office365':
        return office365SignUpUrl;
      case 'slack':
        return `/oauth2/slack_signup?slack_notifications_v2=true&agenda_ai=true${
          redirectUrl ? `&redirect_url=${redirectUrl}` : ''
        }`;
      default:
        return;
    }
  };

  const handleClick = useCallback(() => {
    if (onClick) {
      onClick();
    }

    const authUrl = getAuthUrl() + additionalQueryParams;

    if (withCalendar && (oauthProvider === 'google' || oauthProvider === 'office365')) {
      trackClickedConnectApp(
        oauthProvider === 'google' ? DataSourceSlugs.googleCalendarWorkspaceSync : DataSourceSlugs.office365Calendar
      );
    }

    if (oauthProvider === 'slack') {
      trackClickedConnectApp(DataSourceSlugs.slackAi);
    }

    switch (id) {
      case 'zoom':
        (window as any).zoomSdk.openUrl({ url: `${window.location.origin}/${authUrl}` });
        break;
      case 'chrome':
        window.open(authUrl);
        break;
      default:
        window.location.href = authUrl;
    }
  }, []);

  const LogoImgSrc = () => {
    switch (oauthProvider) {
      case 'google':
        return GoogleLogo;
      case 'office365':
        return Office365Logo;
      case 'slack':
        return SlackLogo;
      case 'pi':
        return Illustrations.pi_logo_circle_red;
      default:
        return null;
    }
  };

  const IconName = () => {
    switch (oauthProvider) {
      case 'google':
        return 'google';
      case 'office365':
        return 'microsoft';
      case 'slack':
        return 'slack';
      case 'pi':
        return 'pi';
      default:
        return null;
    }
  };

  const logo = (() => {
    if (iconType === 'none') return null;
    if (iconType === 'icon') return <WpIcon prefix="fab" name={IconName()} />;
    return <img src={LogoImgSrc()} className={`OAuthButton__logo-img OAuthButton__logo-img--${size}`} />;
  })();

  return (
    <WpButton variant={variant} size={size} color={color} onClick={handleClick} block={block} qaTest={qaTest}>
      {logo}
      {text}
    </WpButton>
  );
};

export default OAuthButton;
